import type { IUpdateParentPreferencesPayload } from '@/types/payload';

import { useState, useEffect, useCallback } from 'react';

import Button from '@/components/Button/Button';
import Select from '@/components/Select';
import SettingsSkeleton from '@/components/SkeletonLoader/SettingsSkeleton';
import { Switch } from '@/components/Switch';
import { useFormatText } from '@/hooks/useFormatText';
import {
  getParentChildren,
  getParentPreferences,
  updateParentPreferences,
} from '@/service/parentSettingsServices';
import { useParentStore } from '@/store/useParentStore';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { getAssetUrl } from '@/utils';

interface IChildOption {
  value: string;
  label: string;
  age: number;
  profile_pic?: string | null;
}

interface IDefaultChild {
  id: string;
  name: string;
  profile_pic: string | null;
  age: number;
}

interface IParentPreferencesData {
  weekly_summary_email: boolean;
  default_child: IDefaultChild | null;
}

const Preference = () => {
  const { formatText } = useFormatText();
  const { isScreenLoading, setScreenLoading } = useSkeletonStore();
  const setSelectedChild = useParentStore((state) => state.setSelectedChild);

  const [childrenOptions, setChildrenOptions] = useState<IChildOption[]>([]);
  const [selectedDefaultChild, setSelectedDefaultChild] = useState<string>('');
  const [weeklySummaryEnabled, setWeeklySummaryEnabled] =
    useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const fetchChildren = useCallback(async (): Promise<IChildOption[]> => {
    try {
      const res = await getParentChildren();
      if (res?.status && res?.data) {
        const children = res.data as IDefaultChild[];
        const options: IChildOption[] = children?.map((child) => ({
          value: child?.id,
          label: child?.name,
          age: child.age ?? 0,
          profile_pic: getAssetUrl(child?.profile_pic) || null,
        }));
        setChildrenOptions(options);
        return options;
      }
    } catch (error) {
      console.error('Failed to fetch children:', error);
    }
    return [];
  }, []);

  const fetchPreferences = useCallback(
    async (availableChildren: IChildOption[]) => {
      try {
        const res = await getParentPreferences();
        if (res?.status && res?.data) {
          const data = res.data as IParentPreferencesData;
          const childId = data.default_child?.id || '';
          const isValidChild = availableChildren.some(
            (opt) => opt.value === childId
          );
          setSelectedDefaultChild(
            isValidChild ? childId : availableChildren[0]?.value || ''
          );
          setWeeklySummaryEnabled(data.weekly_summary_email ?? false);
        }
      } catch (error) {
        console.error('Failed to fetch preferences:', error);
      }
    },
    []
  );

  useEffect(() => {
    const loadData = async () => {
      setScreenLoading(true);
      try {
        const options = await fetchChildren();
        await fetchPreferences(options);
      } finally {
        setScreenLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchChildren, fetchPreferences]);

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const payload: IUpdateParentPreferencesPayload = {
        weekly_summary_email: weeklySummaryEnabled,
        ...(selectedDefaultChild && { default_child_id: selectedDefaultChild }),
      };
      const response = await updateParentPreferences(payload);
      if (response?.status && selectedDefaultChild) {
        // Sync default child to global store so dashboard refreshes
        const childOption = childrenOptions?.find(
          (opt) => opt.value === selectedDefaultChild
        );
        if (childOption) {
          setSelectedChild({
            id: childOption?.value,
            name: childOption?.label,
            age: childOption?.age,
            profile_pic: childOption?.profile_pic ?? undefined,
          });
        }
      }
    } catch (error) {
      console.error('Failed to update preferences:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isScreenLoading) {
    return <SettingsSkeleton />;
  }

  return (
    <div className="flex flex-col gap-6 w-full max-w-217">
      {/* Heading */}
      <h2 className="text-xl font-semibold text-neutral-primary tracking-[-0.2px] leading-7">
        {formatText('settings.preferences.title')}
      </h2>

      {/* Preferences Card */}
      <div className="border border-neutral-light-200 rounded-2xl p-4 flex flex-col items-start w-full">
        {/* Row 1: Default Child View */}
        <div className="flex gap-12.5 items-start justify-between pb-6 border-b border-neutral-light-200 w-full">
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <p className="text-base font-semibold text-neutral-primary leading-6">
              {formatText('settings.preferences.default-child-view')}
            </p>
            <p className="text-sm text-neutral-secondary leading-5">
              {formatText(
                'settings.preferences.default-child-view-description'
              )}
            </p>
          </div>
          <div className="w-87.5 shrink-0">
            <Select
              options={childrenOptions}
              selected={selectedDefaultChild}
              onChange={(val) => setSelectedDefaultChild(val as string)}
              placeholder={formatText(
                'settings.preferences.default-child-view'
              )}
            />
          </div>
        </div>

        {/* Row 2: Weekly Summary Email */}
        <div className="flex gap-12.5 items-start justify-between pt-4 w-full">
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <p className="text-base font-semibold text-neutral-primary leading-6">
              {formatText('settings.preferences.weekly-summary-email')}
            </p>
            <p className="text-sm text-neutral-secondary leading-5">
              {formatText(
                'settings.preferences.weekly-summary-email-description'
              )}
            </p>
          </div>
          <Switch
            id="weekly-summary-switch"
            checked={weeklySummaryEnabled}
            onChange={setWeeklySummaryEnabled}
          />
        </div>
      </div>

      {/* Save Button */}
      <Button
        label={formatText('common.actions.save-changes')}
        variant="primary"
        loading={isSaving}
        onClick={handleSaveChanges}
        className="w-fit"
      />
    </div>
  );
};

export default Preference;
